import type { InputState } from '../core/types';
import { IDLE_INPUT } from '../core/types';
import { clamp } from '../core/math';
import './touch.css';

export interface TouchOptions {
  onPause?: () => void;
}

export interface TouchControls {
  element: HTMLElement;
  state: InputState;
  setSpeed(kmh: number): void;
  hide(): void;
  show(): void;
  destroy(): void;
}

const buzz = (ms = 8): void => {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // nem minden böngésző támogatja
  }
};

/**
 * Telefonos vezérlés: bal hüvelykujj alatt analóg kormánykarika,
 * jobb oldalon gáz/fék/drift/tárgy/képesség gombok + szünet és teljes képernyő.
 */
export function createTouchControls(root: HTMLElement, opts: TouchOptions = {}): TouchControls {
  const state: InputState = { ...IDLE_INPUT };
  const el = document.createElement('div');
  el.className = 'touch';

  // --- fekvő tájolás figyelmeztetés ---
  const rotate = document.createElement('div');
  rotate.className = 'touch__rotate';
  const phone = document.createElement('div');
  phone.className = 'touch__phone';
  const rotateTitle = document.createElement('strong');
  rotateTitle.textContent = 'Fordítsd el a telefont';
  const rotateSub = document.createElement('span');
  rotateSub.textContent = 'A verseny fekvő módban játszható. A menük állóban is rendben vannak.';
  rotate.append(phone, rotateTitle, rotateSub);

  // --- felső gombok ---
  const top = document.createElement('div');
  top.className = 'touch__top';
  const pause = document.createElement('button');
  pause.type = 'button';
  pause.className = 'touch__chip';
  pause.textContent = 'II';
  pause.setAttribute('aria-label', 'Szünet');
  const fs = document.createElement('button');
  fs.type = 'button';
  fs.className = 'touch__chip';
  fs.textContent = '⛶';
  fs.setAttribute('aria-label', 'Teljes képernyő');
  top.append(pause, fs);

  // --- kormány ---
  const steerPad = document.createElement('div');
  steerPad.className = 'touch__steer';
  const knob = document.createElement('div');
  knob.className = 'touch__knob';
  const steerLabel = document.createElement('span');
  steerLabel.className = 'touch__label';
  steerLabel.textContent = 'KORMÁNY';
  steerPad.append(knob, steerLabel);

  // --- gombok ---
  const buttons = document.createElement('div');
  buttons.className = 'touch__buttons';
  const makeButton = (control: string, label: string, className: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `touch__btn ${className}`;
    b.dataset.control = control;
    b.textContent = label;
    b.setAttribute('aria-label', label);
    buttons.appendChild(b);
    return b;
  };
  const gas = makeButton('gas', 'GÁZ', 'touch__btn--gas');
  const gasLabel = document.createElement('span');
  gasLabel.className = 'touch__btn-label';
  gasLabel.textContent = 'GÁZ';
  const gasSpeed = document.createElement('span');
  gasSpeed.className = 'touch__speed';
  gasSpeed.textContent = '0';
  gas.replaceChildren(gasLabel, gasSpeed);
  const brake = makeButton('brake', 'FÉK', 'touch__btn--brake');
  const drift = makeButton('drift', 'DRIFT', 'touch__btn--drift');
  const item = makeButton('item', 'TÁRGY', 'touch__btn--item');
  const ability = makeButton('ability', 'KÉP.', 'touch__btn--ability');

  el.append(rotate, top, steerPad, buttons);
  root.appendChild(el);

  let steerPointer: number | null = null;

  const setSteer = (clientX: number): void => {
    const rect = steerPad.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const max = rect.width * 0.33;
    const dx = clamp(clientX - center, -max, max);
    state.steer = dx / max;
    knob.style.transform = `translateX(${dx}px)`;
  };

  const endSteer = (): void => {
    steerPointer = null;
    state.steer = 0;
    knob.style.transform = 'translateX(0)';
    steerPad.classList.remove('is-active');
  };

  const onSteerDown = (e: PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    try {
      steerPad.setPointerCapture(e.pointerId);
    } catch {
      // szintetikus/egér esemény: nincs aktív pointer
    }
    steerPointer = e.pointerId;
    steerPad.classList.add('is-active');
    setSteer(e.clientX);
    buzz(6);
  };
  const onSteerMove = (e: PointerEvent): void => {
    if (e.pointerId !== steerPointer) return;
    e.preventDefault();
    setSteer(e.clientX);
  };
  const onSteerUp = (e: PointerEvent): void => {
    if (e.pointerId !== steerPointer) return;
    e.preventDefault();
    endSteer();
  };
  steerPad.addEventListener('pointerdown', onSteerDown);
  steerPad.addEventListener('pointermove', onSteerMove);
  steerPad.addEventListener('pointerup', onSteerUp);
  steerPad.addEventListener('pointercancel', onSteerUp);
  steerPad.addEventListener('lostpointercapture', endSteer);

  const holdButton = (btn: HTMLButtonElement, on: () => void, off: () => void): void => {
    let pointer: number | null = null;
    const down = (e: PointerEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      pointer = e.pointerId;
      try {
        btn.setPointerCapture(e.pointerId);
      } catch {
        // szintetikus esemény
      }
      btn.classList.add('is-active');
      on();
      buzz(7);
    };
    const up = (e: PointerEvent): void => {
      if (pointer !== null && e.pointerId !== pointer) return;
      e.preventDefault();
      pointer = null;
      btn.classList.remove('is-active');
      off();
    };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', up);
    btn.addEventListener('lostpointercapture', up);
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  };

  holdButton(
    gas,
    () => {
      state.throttle = 1;
    },
    () => {
      state.throttle = 0;
    },
  );
  holdButton(
    brake,
    () => {
      state.brake = 1;
    },
    () => {
      state.brake = 0;
    },
  );
  holdButton(
    drift,
    () => {
      state.drift = true;
    },
    () => {
      state.drift = false;
    },
  );

  const tapButton = (btn: HTMLButtonElement, fire: () => void): void => {
    const down = (e: PointerEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      fire();
      btn.classList.add('is-active');
      buzz(10);
      window.setTimeout(() => btn.classList.remove('is-active'), 140);
    };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  };
  tapButton(item, () => {
    state.useItem = true;
  });
  tapButton(ability, () => {
    state.ability = true;
  });

  pause.addEventListener('click', (e) => {
    e.preventDefault();
    opts.onPause?.();
  });
  fs.addEventListener('click', async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      const orientation = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
      await orientation.lock?.('landscape');
    } catch {
      // iOS/Safari: marad a böngésző nézet
    }
  });

  const releaseAll = (): void => {
    state.throttle = 0;
    state.brake = 0;
    state.drift = false;
    state.useItem = false;
    state.ability = false;
    state.steer = 0;
    knob.style.transform = 'translateX(0)';
    for (const b of [gas, brake, drift, item, ability]) b.classList.remove('is-active');
    steerPad.classList.remove('is-active');
    steerPointer = null;
  };

  const onVisibility = (): void => {
    if (document.hidden) releaseAll();
  };
  const onOrientation = (): void => releaseAll();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('blur', releaseAll);
  window.addEventListener('orientationchange', onOrientation);

  el.addEventListener('contextmenu', (e) => e.preventDefault());

  return {
    element: el,
    state,
    setSpeed: (kmh: number) => {
      gasSpeed.textContent = String(Math.max(0, Math.round(kmh)));
    },
    hide: () => {
      releaseAll();
      el.classList.add('is-hidden');
    },
    show: () => {
      releaseAll();
      el.classList.remove('is-hidden');
    },
    destroy: () => {
      releaseAll();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', releaseAll);
      window.removeEventListener('orientationchange', onOrientation);
      el.remove();
    },
  };
}
